import React, { useState } from 'react';
import {
  Menu, MenuButton, MenuList, MenuItem, MenuDivider, MenuGroup,
  Button, IconButton, useToast, useBreakpointValue, Tooltip, HStack,
} from '@chakra-ui/react';
import {
  LuCopy, LuDownload, LuPrinter, LuShare2, LuChevronDown, LuFileText, LuType, LuLink,
} from 'react-icons/lu';
import { docParaMarkdown, docParaTexto, baixarArquivoTexto, nomeArquivoSeguro } from './documentUtils';

/**
 * Ações do documento: copiar, exportar .md, exportar PDF (impressão do
 * navegador) e copiar link interno. O link não é público — só abre para
 * quem já tem acesso à wiki.
 */
const WikiDocActions = ({ item, colorScheme = 'orange', onPrint }) => {
  const toast = useToast();
  const [ocupado, setOcupado] = useState(false);
  const compacto = useBreakpointValue({ base: true, md: false });

  const avisar = (titulo, status = 'success') =>
    toast({ title: titulo, status, duration: 2000, position: 'top-right', isClosable: true });

  const copiar = async (texto, rotulo) => {
    if (!texto) {
      avisar('Nada para copiar', 'warning');
      return;
    }
    try {
      await navigator.clipboard.writeText(texto);
      avisar(rotulo);
    } catch {
      avisar('Não foi possível copiar', 'error');
    }
  };

  const copiarMarkdown = () => copiar(docParaMarkdown(item.content), 'Markdown copiado!');
  const copiarTexto = () => copiar(docParaTexto(item.content) || item.description, 'Texto copiado!');
  const copiarResposta = () => copiar(item.standardResponse, 'Resposta copiada!');

  const copiarLink = () => {
    const url = `${window.location.origin}/wiki/${item.id}`;
    copiar(url, 'Link copiado! Abre para quem tem acesso à wiki.');
  };

  const exportarMd = () => {
    const md = docParaMarkdown(item.content);
    if (!md) {
      avisar('Documento vazio', 'warning');
      return;
    }
    baixarArquivoTexto(nomeArquivoSeguro(item.title, 'md'), `# ${item.title}\n\n${md}\n`);
    avisar('Arquivo .md baixado!');
  };

  const exportarPdf = () => {
    setOcupado(true);
    // A impressão do navegador gera PDF com texto vetorial e links preservados.
    onPrint?.();
    setTimeout(() => {
      window.print();
      setOcupado(false);
    }, 250);
  };

  return (
    <HStack spacing={2}>
      <Tooltip label="Exportar em PDF (impressão)" fontSize="xs" openDelay={400}>
        <IconButton
          aria-label="Exportar PDF"
          icon={<LuPrinter />}
          size="sm"
          variant="outline"
          colorScheme={colorScheme}
          isLoading={ocupado}
          onClick={exportarPdf}
        />
      </Tooltip>

      <Menu placement="bottom-end">
        {compacto ? (
          <MenuButton as={IconButton} aria-label="Ações do documento" icon={<LuShare2 />} size="sm" variant="outline" colorScheme={colorScheme} />
        ) : (
          <MenuButton as={Button} size="sm" variant="outline" colorScheme={colorScheme} leftIcon={<LuShare2 />} rightIcon={<LuChevronDown />} fontWeight="normal">
            Compartilhar
          </MenuButton>
        )}
        <MenuList fontSize="sm" zIndex="popover">
          <MenuGroup title="Copiar" fontSize="xs" color="gray.500">
            <MenuItem icon={<LuLink />} onClick={copiarLink}>Link desta página</MenuItem>
            <MenuItem icon={<LuFileText />} onClick={copiarMarkdown}>Conteúdo em Markdown</MenuItem>
            <MenuItem icon={<LuType />} onClick={copiarTexto}>Conteúdo em texto</MenuItem>
            {item.standardResponse && (
              <MenuItem icon={<LuCopy />} onClick={copiarResposta}>Resposta para o cliente</MenuItem>
            )}
          </MenuGroup>
          <MenuDivider />
          <MenuGroup title="Exportar" fontSize="xs" color="gray.500">
            <MenuItem icon={<LuDownload />} onClick={exportarMd}>Arquivo .md</MenuItem>
            <MenuItem icon={<LuPrinter />} onClick={exportarPdf}>PDF (impressão)</MenuItem>
          </MenuGroup>
        </MenuList>
      </Menu>
    </HStack>
  );
};

export default WikiDocActions;
